/** Auth context: resolves the current clinician/admin session via the cookie. */
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, setCsrfToken } from './api.js';
import type { Role } from '@toxofollow/shared';

export interface AuthUser {
  id: string;
  role: Role;
  name: string;
  locale: string;
  email: string | null;
  phone: string | null;
}

interface AuthValue {
  user: AuthUser | null;
  loading: boolean;
  login: (email: string, password: string, totp?: string) => Promise<void>;
  requestOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, code: string) => Promise<void>;
  acceptInvite: (token: string, password?: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ user: AuthUser; csrfToken: string }>('/auth/me');
      setUser(me.user);
      setCsrfToken(me.csrfToken);
    } catch {
      setUser(null);
      setCsrfToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (email: string, password: string, totp?: string) => {
    const res = await api.post<{ user: AuthUser; csrfToken: string }>('/auth/clinician/login', { email, password, totp });
    setUser(res.user);
    setCsrfToken(res.csrfToken);
  }, []);

  const requestOtp = useCallback(async (phone: string) => {
    await api.post('/auth/otp/request', { phone });
  }, []);

  const verifyOtp = useCallback(async (phone: string, code: string) => {
    const res = await api.post<{ user: AuthUser; csrfToken: string }>('/auth/otp/verify', { phone, code });
    setUser(res.user);
    setCsrfToken(res.csrfToken);
  }, []);

  const acceptInvite = useCallback(async (token: string, password?: string) => {
    const res = await api.post<{ user: AuthUser; csrfToken: string }>('/auth/invite/accept', { token, password });
    setUser(res.user);
    setCsrfToken(res.csrfToken);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    await api.post('/auth/change-password', { currentPassword, newPassword });
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {});
    setUser(null);
    setCsrfToken(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, requestOtp, verifyOtp, acceptInvite, changePassword, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
